export const fragmentShader = `
uniform float uOpacity;
uniform float uTime;
uniform float uBrightnessFlickerScale;
uniform float uBrightnessFlickerAmount;
uniform float uGrayscale;
uniform float uContrast;
uniform float uBrightness;
uniform float uDitherEnabled;
uniform float uDitherScale;

varying vec3 vColor;
varying vec2 vUv;

// Simplex noise for brightness flickering
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                      -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

// Bayer 4x4 dither matrix
float bayer4x4(vec2 pos) {
  int x = int(mod(pos.x, 4.0));
  int y = int(mod(pos.y, 4.0));
  int index = x + y * 4;
  
  // Bayer matrix values (0-15 normalized to 0-1)
  float matrix[16];
  matrix[0] = 0.0/16.0;   matrix[1] = 8.0/16.0;   matrix[2] = 2.0/16.0;   matrix[3] = 10.0/16.0;
  matrix[4] = 12.0/16.0;  matrix[5] = 4.0/16.0;   matrix[6] = 14.0/16.0;  matrix[7] = 6.0/16.0;
  matrix[8] = 3.0/16.0;   matrix[9] = 11.0/16.0;  matrix[10] = 1.0/16.0;  matrix[11] = 9.0/16.0;
  matrix[12] = 15.0/16.0; matrix[13] = 7.0/16.0;  matrix[14] = 13.0/16.0; matrix[15] = 5.0/16.0;
  
  for (int i = 0; i < 16; i++) {
    if (i == index) return matrix[i];
  }
  return 0.0;
}
  
void main() {
  vec3 color = vColor;
  
  // Brightness fluctuation noise
  float brightnessNoise = snoise(vUv * uBrightnessFlickerScale + vec2(uTime));
  float noiseMult = 1.0 + brightnessNoise * uBrightnessFlickerAmount;
  
  // Apply brightness with fluctuation
  color *= uBrightness * noiseMult;
  
  // Apply contrast
  color = (color - 0.5) * uContrast + 0.5;
  
  // Grayscale conversion
  float gray = dot(color, vec3(0.299, 0.587, 0.114));
  color = mix(color, vec3(gray), uGrayscale);
  
  // Dithering
  if (uDitherEnabled > 0.5) {
    vec2 pixelPos = gl_FragCoord.xy / uDitherScale;
    float threshold = bayer4x4(pixelPos);
    
  // Get luminance for threshold comparison
    float luminance = dot(color, vec3(0.299, 0.587, 0.114));
    
    // Discard pixels below threshold (creates stipple/halftone effect)
    if (luminance < threshold) {
      discard;
    }

    color = vec3(1.0);
  }
  
  // Clamp colors
  color = clamp(color, 0.0, 1.0);
  
  gl_FragColor = vec4(color, uOpacity);
}
`;
